import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import DriverCardPicker, { PodiumPicker } from '../components/DriverCardPicker';
import CircuitMap from '../components/CircuitMap';
import Icon from '../components/Icon';
import SuccessOverlay from '../components/SuccessOverlay';

const EMPTY_PICKS = {
  winner: '', p2: '', p3: '',
  polePosition: '', fastestLap: '', fastestQualifyingLap: '',
  q1Eliminated: [], q2Eliminated: [],
  safetyCar: null, virtualSafetyCar: null, redFlag: null,
};

const YesNoToggle = ({ label, value, onChange, disabled, points }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="flex-1 min-w-0">
      <p className="text-white text-sm font-semibold">{label}</p>
      <p className="text-zinc-500 text-xs">{points} pts si correct</p>
    </div>
    <div className="flex gap-2 shrink-0">
      <button
        type="button"
        onClick={() => !disabled && onChange(true)}
        disabled={disabled}
        className={`px-4 py-1.5 rounded-lg text-sm font-bold border transition-all ${
          value === true
            ? 'bg-green-600 border-green-500 text-white'
            : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'
        } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      >Oui</button>
      <button
        type="button"
        onClick={() => !disabled && onChange(false)}
        disabled={disabled}
        className={`px-4 py-1.5 rounded-lg text-sm font-bold border transition-all ${
          value === false
            ? 'bg-red-700 border-red-600 text-white'
            : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'
        } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
      >Non</button>
    </div>
  </div>
);

const EMPTY_SPRINT_PICKS = { winner: '', p2: '', p3: '' };

const BOOST_OPTIONS = [1, 2, 5, 10];
const EMPTY_BOOSTS = {
  winner: 1, p2: 1, p3: 1,
  polePosition: 1, fastestLap: 1, fastestQualifyingLap: 1,
  safetyCar: 1, virtualSafetyCar: 1, redFlag: 1,
  q1Eliminated: 1, q2Eliminated: 1,
};

const BOOST_META = [
  { key: 'winner',               label: 'Vainqueur',              base: 25 },
  { key: 'p2',                   label: 'P2',                     base: 18 },
  { key: 'p3',                   label: 'P3',                     base: 15 },
  { key: 'polePosition',         label: 'Pole Position',          base: 10 },
  { key: 'fastestLap',           label: 'Meilleur tour course',   base: 12 },
  { key: 'fastestQualifyingLap', label: 'Meilleur tour qualif',   base: 8  },
  { key: 'safetyCar',            label: 'Safety Car',             base: 4  },
  { key: 'virtualSafetyCar',     label: 'Virtual Safety Car',     base: 2  },
  { key: 'redFlag',              label: 'Drapeau rouge',          base: 10 },
  { key: 'q1Eliminated',         label: 'Q1 éliminés (×pilote)',  base: 1,  nopenalty: true },
  { key: 'q2Eliminated',         label: 'Q2 éliminés (×pilote)',  base: 2,  nopenalty: true },
];

const BOOST_COLORS = { 1: null, 2: '#60a5fa', 5: '#f97316', 10: '#e10600' };

const BoostRow = ({ meta, value, onChange, disabled }) => {
  const mult = value || 1;
  const gain = meta.base * mult;
  const penalty = (mult > 1 && !meta.nopenalty) ? -Math.round(meta.base / mult) : null;
  return (
    <div className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{meta.label}</p>
        <p className="text-[11px] mt-0.5">
          <span className="text-green-400">+{gain} pts</span>
          {penalty !== null && <span className="text-red-400 ml-2">{penalty} pts si raté</span>}
          {meta.nopenalty && mult > 1 && <span className="text-zinc-500 ml-2">sans pénalité</span>}
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        {BOOST_OPTIONS.map(b => {
          const active = mult === b;
          const c = BOOST_COLORS[b];
          return (
            <button key={b} type="button"
              onClick={() => !disabled && onChange(b)}
              disabled={disabled}
              className={`w-9 h-7 rounded text-xs font-black border transition-all disabled:opacity-40 ${
                active
                  ? 'text-white border-transparent'
                  : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500'
              }`}
              style={active && c ? { backgroundColor: c, borderColor: c } : active ? { backgroundColor: '#3f3f46', borderColor: '#52525b' } : {}}>
              ×{b}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between pb-2 border-b border-zinc-800"
      >
        <h2 className="font-bold text-white text-base flex items-center gap-2">{title}</h2>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} className="text-zinc-500" />
      </button>
      {open && <div className="flex flex-col gap-4 mt-4">{children}</div>}
    </div>
  );
};

/* ─── Replay component ─────────────────────────────────────── */
const SCORE_VERDICT = (pts) =>
  pts >= 60 ? { icon: '🏆', label: 'Génie du paddock',             color: '#facc15' }
: pts >= 35 ? { icon: '🎯', label: "Bon coup d'œil",               color: '#4ade80' }
: pts >= 15 ? { icon: '📊', label: 'Dans la moyenne',               color: '#60a5fa' }
:             { icon: '😅', label: 'On a tous nos mauvais week-ends', color: '#71717a' };

const normCode = (v) => v ? String(v).toUpperCase().trim() : '';
const VALID_BOOSTS_DISPLAY = [1, 2, 5, 10];
const BOOST_COLOR = { 2: { bg: '#1d4ed822', text: '#60a5fa', border: '#1d4ed855' }, 5: { bg: '#c2410c22', text: '#f97316', border: '#c2410c55' }, 10: { bg: '#9f121222', text: '#e10600', border: '#9f121255' } };

const PronosticReplay = ({ picks, sprintPicks, boosts, isDivine, score, sprintScore, race, liveResults, drivers }) => {
  const res    = race?.results || {};
  const sprint = race?.sprintResults || {};
  const total  = (score ?? 0) + (sprintScore ?? 0);
  const verdict = SCORE_VERDICT(total);
  const bst = boosts || {};

  const driverMap = {};
  for (const d of (drivers || [])) {
    const key = (d.driverId || '').toLowerCase();
    if (key) driverMap[key] = d;
    if (d.code) driverMap[d.code.toLowerCase()] = d;
  }
  for (const s of (liveResults?.standings || [])) {
    if (s.code) driverMap[s.code.toLowerCase()] = { familyName: s.fullName?.split(' ').slice(-1)[0], givenName: s.fullName?.split(' ')[0], fullName: s.fullName };
  }
  const dName = (code) => {
    if (!code) return null;
    const d = driverMap[code.toLowerCase()];
    if (!d) return code.toUpperCase();
    return d.familyName ? `${d.givenName?.[0] || ''}.${d.givenName?.[0] ? ' ' : ''}${d.familyName}` : (d.fullName || code.toUpperCase());
  };

  const results = {
    winner:               res.winner               || liveResults?.standings?.[0]?.code || null,
    p2:                   res.p2                   || liveResults?.standings?.[1]?.code || null,
    p3:                   res.p3                   || liveResults?.standings?.[2]?.code || null,
    polePosition:         res.polePosition         || liveResults?.pole?.code          || null,
    fastestLap:           res.fastestLap           || liveResults?.fastestLap?.code    || null,
    fastestQualifyingLap: res.fastestQualifyingLap || null,
    q1Eliminated: res.q1Eliminated?.length  ? res.q1Eliminated  : (liveResults?.q1Eliminated  || []),
    q2Eliminated: res.q2Eliminated?.length  ? res.q2Eliminated  : (liveResults?.q2Eliminated  || []),
    safetyCar:        res.safetyCar        ?? liveResults?.safetyCar        ?? null,
    virtualSafetyCar: res.virtualSafetyCar ?? liveResults?.virtualSafetyCar ?? null,
    redFlag:          res.redFlag          ?? liveResults?.redFlag          ?? null,
  };

  const hasResults = !!(results.winner || results.polePosition || results.q1Eliminated?.length);

  const getBoost = (key) => {
    if (isDivine) return null;
    const b = Number(bst[key]);
    return VALID_BOOSTS_DISPLAY.includes(b) ? b : 1;
  };
  const calcPts = (base, correct, boost) => {
    if (isDivine) return correct ? base * 4 : 0;
    if (boost <= 1) return correct ? base : 0;
    return correct ? base * boost : -Math.round(base / boost);
  };

  const SectionHeader = ({ icon, title }) => (
    <div className="flex items-center gap-2 pb-2 mb-1 border-b border-zinc-800">
      <span className="text-sm">{icon}</span>
      <span className="text-[11px] font-black uppercase tracking-widest text-zinc-400">{title}</span>
    </div>
  );

  const BoostBadge = ({ boost, base, correct, pts }) => {
    if (!boost || boost <= 1) return null;
    const c = BOOST_COLOR[boost] || BOOST_COLOR[10];
    return (
      <div className="flex items-center gap-2 mt-1" style={{ paddingLeft: '7.5rem' }}>
        <span className="text-[10px] px-1.5 py-0.5 rounded font-black" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>×{boost}</span>
        {correct === true  && <span className="text-zinc-500 text-[10px]">base {base} × {boost} = +{pts} pts</span>}
        {correct === false && <span className="text-zinc-500 text-[10px]">pénalité −{Math.round(base / boost)} pts (raté avec ×{boost})</span>}
      </div>
    );
  };

  const DivineBadge = ({ base }) => isDivine ? (
    <div className="flex items-center gap-1 mt-1" style={{ paddingLeft: '7.5rem' }}>
      <span className="text-[10px] text-yellow-500/60">⚡ Mode Divin × 4 — base {base} pts</span>
    </div>
  ) : null;

  const PickRow = ({ label, pick, result, base, boostKey, type = 'driver' }) => {
    const hasPick = pick !== null && pick !== undefined && pick !== '' && !(Array.isArray(pick) && !pick.length);
    if (!hasPick) return null;
    const boost = getBoost(boostKey);
    const noResult = (type === 'bool') ? (result === null || result === undefined) : !result;
    const correct  = noResult ? null : (type === 'bool') ? (pick === result) : (normCode(pick) === normCode(result));
    const pts      = correct !== null ? calcPts(base, correct, boost ?? 1) : null;
    const pickLabel = type === 'bool' ? (pick ? 'Oui' : 'Non') : dName(pick);
    const realLabel = noResult ? null : type === 'bool' ? (result ? 'Oui' : 'Non') : dName(result);
    return (
      <div className="py-2.5 border-b border-zinc-800/50 last:border-0">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-xs shrink-0" style={{ width: '7rem' }}>{label}</span>
          <span className="text-white text-sm font-semibold flex-1 truncate">{pickLabel}</span>
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <span className="text-zinc-700 text-xs">→</span>
            <span className={`text-sm font-medium truncate ${
              noResult ? 'text-zinc-600 italic' : correct === true ? 'text-green-400' : correct === false ? 'text-red-400/80' : 'text-zinc-400'
            }`}>{realLabel ?? '—'}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {correct === true  && <span className="text-green-500 text-xs font-black">✓</span>}
            {correct === false && <span className="text-red-500  text-xs font-black">✗</span>}
            {correct === null  && <span className="text-zinc-700 text-xs">—</span>}
            <span className={`text-xs font-black min-w-[3rem] text-right ${
              pts > 0 ? 'text-green-400' : pts < 0 ? 'text-red-400' : 'text-zinc-600'
            }`}>
              {pts === null ? '' : pts > 0 ? `+${pts} pts` : pts === 0 ? '0 pt' : `${pts} pts`}
            </span>
          </div>
        </div>
        <BoostBadge boost={boost} base={base} correct={correct} pts={pts} />
        <DivineBadge base={base} />
      </div>
    );
  };

  const EliminatedRow = ({ label, elPicks, resultCodes, base, boostKey }) => {
    if (!elPicks?.length) return null;
    const boost = getBoost(boostKey);
    const noResult = !resultCodes?.length;
    const matches = noResult ? [] : elPicks.filter(p => resultCodes.some(r => normCode(r) === normCode(p)));
    const pts = noResult ? null : matches.length * base * (boost ?? 1);
    const c = boost > 1 ? (BOOST_COLOR[boost] || BOOST_COLOR[10]) : null;
    return (
      <div className="py-2.5 border-b border-zinc-800/50 last:border-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-zinc-500 text-xs shrink-0" style={{ width: '7rem' }}>{label}</span>
          <span className="text-zinc-400 text-xs flex-1">{elPicks.length} pilote{elPicks.length > 1 ? 's' : ''} cochés</span>
          <span className={`text-xs font-black min-w-[3rem] text-right ${pts > 0 ? 'text-green-400' : 'text-zinc-600'}`}>
            {pts === null ? '—' : pts > 0 ? `+${pts} pts` : '0 pt'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5" style={{ paddingLeft: '7.5rem' }}>
          {elPicks.map(p => {
            const hit = !noResult && resultCodes.some(r => normCode(r) === normCode(p));
            const pending = noResult;
            return (
              <span key={p} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${
                pending ? 'bg-zinc-800 text-zinc-500 border-zinc-700'
                : hit    ? 'bg-green-900/40 text-green-400 border-green-800'
                         : 'bg-zinc-800/60 text-zinc-600 border-zinc-700/60 line-through'
              }`}>
                {!pending && (hit ? '✓ ' : '✗ ')}{dName(p)}
              </span>
            );
          })}
        </div>
        {!noResult && (
          <div className="flex items-center gap-2 mt-1.5" style={{ paddingLeft: '7.5rem' }}>
            {boost > 1 && c && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-black" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>×{boost}</span>
            )}
            <span className="text-zinc-600 text-[10px]">
              {matches.length}/{elPicks.length} correct{matches.length > 1 ? 's' : ''}
              {boost > 1 ? ` × ${base} pts × ${boost} boost` : ` × ${base} pt${base > 1 ? 's' : ''}`}
              {isDivine ? ' × 4 (divin)' : ''}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (!hasResults) {
    return (
      <div className="mt-8 card text-center py-10 text-zinc-500 text-sm">
        <p className="text-4xl mb-3">⏳</p>
        Résultats officiels pas encore disponibles.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-8">
      {/* Score banner */}
      <div className="rounded-xl border p-5 flex items-center justify-between"
        style={{ borderColor: '#e1060044', background: 'linear-gradient(135deg,#e1060012 0%,transparent 60%)' }}>
        <div>
          <p className="text-zinc-400 text-xs uppercase tracking-widest mb-1">Score final</p>
          <p className="text-5xl font-black text-white leading-none">
            {total}<span className="text-lg text-zinc-500 font-normal ml-1">pts</span>
          </p>
          {isDivine && <p className="text-yellow-400 text-xs mt-1">⚡ Mode Divin activé (×4)</p>}
          {sprintScore > 0 && <p className="text-orange-400 text-xs mt-0.5">dont {sprintScore} pts sprint</p>}
        </div>
        <div className="text-right">
          <p className="text-4xl mb-1">{verdict.icon}</p>
          <p className="text-sm font-black" style={{ color: verdict.color }}>{verdict.label}</p>
        </div>
      </div>

      {/* Podium */}
      <div className="card">
        <SectionHeader icon="🏆" title="Podium" />
        <PickRow label="Vainqueur" pick={picks.winner} result={results.winner} base={25} boostKey="winner" />
        <PickRow label="P2"        pick={picks.p2}     result={results.p2}     base={18} boostKey="p2" />
        <PickRow label="P3"        pick={picks.p3}     result={results.p3}     base={15} boostKey="p3" />
      </div>

      {/* Qualifications */}
      <div className="card">
        <SectionHeader icon="⚡" title="Qualifications" />
        <PickRow label="Pole Position"   pick={picks.polePosition}         result={results.polePosition}         base={10} boostKey="polePosition" />
        <PickRow label="Meilleur tour Q" pick={picks.fastestQualifyingLap} result={results.fastestQualifyingLap} base={8}  boostKey="fastestQualifyingLap" />
        <EliminatedRow label="Éliminés Q1" elPicks={picks.q1Eliminated} resultCodes={results.q1Eliminated} base={1} boostKey="q1Eliminated" />
        <EliminatedRow label="Éliminés Q2" elPicks={picks.q2Eliminated} resultCodes={results.q2Eliminated} base={2} boostKey="q2Eliminated" />
      </div>

      {/* Course */}
      <div className="card">
        <SectionHeader icon="🏁" title="Course" />
        <PickRow label="Meilleur tour" pick={picks.fastestLap} result={results.fastestLap} base={12} boostKey="fastestLap" />
      </div>

      {/* Événements */}
      <div className="card">
        <SectionHeader icon="🚨" title="Événements" />
        <PickRow label="Safety Car"    pick={picks.safetyCar}        result={results.safetyCar}        base={4}  boostKey="safetyCar"        type="bool" />
        <PickRow label="Virtual SC"    pick={picks.virtualSafetyCar} result={results.virtualSafetyCar} base={2}  boostKey="virtualSafetyCar" type="bool" />
        <PickRow label="Drapeau rouge" pick={picks.redFlag}          result={results.redFlag}          base={10} boostKey="redFlag"          type="bool" />
      </div>

      {/* Sprint */}
      {sprintPicks && (sprintPicks.winner || sprintPicks.p2 || sprintPicks.p3) && sprint.winner && (
        <div className="card">
          <SectionHeader icon="⚡" title="Sprint" />
          <PickRow label="Vainqueur" pick={sprintPicks.winner} result={sprint.winner} base={12} boostKey="_" />
          <PickRow label="P2"        pick={sprintPicks.p2}     result={sprint.p2}     base={10} boostKey="_" />
          <PickRow label="P3"        pick={sprintPicks.p3}     result={sprint.p3}     base={8}  boostKey="_" />
        </div>
      )}
    </div>
  );
};

/* ─── Main page ─────────────────────────────────────────────── */
const Pronostic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [race, setRace] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [picks, setPicks] = useState(EMPTY_PICKS);
  const [sprintPicks, setSprintPicks] = useState(EMPTY_SPRINT_PICKS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSprint, setSavingSprint] = useState(false);
  const [error, setError] = useState('');
  const [sprintError, setSprintError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sprintSuccess, setSprintSuccess] = useState(false);
  const [boosts, setBoosts] = useState(EMPTY_BOOSTS);
  const [score, setScore] = useState(null);
  const [sprintScore, setSprintScore] = useState(null);
  const [liveResults, setLiveResults] = useState(null);
  const [isDivine, setIsDivine] = useState(false);
  const [divineFlash, setDivineFlash] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/races/${id}`),
      api.get(`/races/drivers`),
      api.get(`/pronostics/${id}/me`).catch(() => null),
    ]).then(([raceRes, driversRes, pronosticRes]) => {
      const r = raceRes.data;
      setRace(r);
      setDrivers(driversRes.data);
      if (pronosticRes?.data?.picks) {
        setPicks({ ...EMPTY_PICKS, ...pronosticRes.data.picks });
      }
      if (pronosticRes?.data?.sprintPicks) {
        setSprintPicks({ ...EMPTY_SPRINT_PICKS, ...pronosticRes.data.sprintPicks });
      }
      if (pronosticRes?.data) {
        setScore(pronosticRes.data.score ?? null);
        setSprintScore(pronosticRes.data.sprintScore ?? null);
        if (pronosticRes.data.boosts) setBoosts({ ...EMPTY_BOOSTS, ...pronosticRes.data.boosts });
        if (pronosticRes.data.isDivine) setIsDivine(true);
      }
      const raceDate = r.sessions?.race?.date
        ? new Date(`${r.sessions.race.date}T${r.sessions.race.time || '12:00:00Z'}`) : null;
      if (raceDate && raceDate < new Date()) {
        api.get(`/races/${id}/weekend-results`)
          .then(res => setLiveResults(res.data))
          .catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const divineRoll = () => {
    if (!drivers.length) return;
    const ids = drivers.map(d => d.driverId).filter(Boolean);
    const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);
    const sh = shuffle(ids);
    const winner = sh[0], p2 = sh[1], p3 = sh[2];
    const rest = sh.slice(3);
    const q1Eliminated = rest.slice(0, 5);
    const q2Pool = shuffle(ids.filter(c => !q1Eliminated.includes(c) && c !== winner && c !== p2 && c !== p3));
    const q2Eliminated = q2Pool.slice(0, 5);
    setPicks({
      winner, p2, p3,
      polePosition:         shuffle(ids)[0],
      fastestLap:           shuffle(ids)[0],
      fastestQualifyingLap: shuffle(ids)[0],
      q1Eliminated,
      q2Eliminated,
      safetyCar:        Math.random() > 0.5,
      virtualSafetyCar: Math.random() > 0.5,
      redFlag:          Math.random() > 0.5,
    });
    setIsDivine(true);
    setDivineFlash(true);
    setTimeout(() => setDivineFlash(false), 900);
  };

  const cancelDivine = () => { setPicks(EMPTY_PICKS); setIsDivine(false); };

  const set = (field) => (val) => { setPicks(p => ({ ...p, [field]: val })); setIsDivine(false); };
  const setSprint = (field) => (val) => setSprintPicks(p => ({ ...p, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    const { winner, p2, p3 } = picks;
    if (winner && p2 && p3 && new Set([winner, p2, p3]).size !== 3) {
      setError('Le podium doit contenir 3 pilotes différents.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/pronostics/${id}`, { picks, boosts, isDivine });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleSprintSubmit = async (e) => {
    e.preventDefault();
    setSprintError('');
    setSprintSuccess(false);
    const { winner, p2, p3 } = sprintPicks;
    if (winner && p2 && p3 && new Set([winner, p2, p3]).size !== 3) {
      setSprintError('Le podium sprint doit contenir 3 pilotes différents.');
      return;
    }
    setSavingSprint(true);
    try {
      await api.put(`/pronostics/${id}`, { sprintPicks });
      setSprintSuccess(true);
    } catch (err) {
      setSprintError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSavingSprint(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-zinc-400">Chargement...</div>
  );

  if (!race) return (
    <div className="max-w-4xl mx-auto px-4 py-12 text-center text-zinc-400">Course introuvable.</div>
  );

  const now = new Date();
  const deadline = race.deadline ? new Date(race.deadline) : null;
  const isLocked = race.isLocked || (deadline && now > deadline);
  const raceDate = race.sessions?.race?.date
    ? new Date(`${race.sessions.race.date}T${race.sessions.race.time || '12:00:00Z'}`) : null;
  const isPast = raceDate && raceDate < now;
  const isScored = score !== null;
  const hasSprint = !!(race.sessions?.sprint);
  const sprintDeadline = race.sprintDeadline ? new Date(race.sprintDeadline) : null;
  const isSprintLocked = race.sprintIsLocked || (sprintDeadline && now > sprintDeadline);

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <Link to={`/grand-prix/${id}`} className="text-zinc-500 hover:text-zinc-300 text-sm flex items-center gap-1 mb-6">
        ← Retour au Grand Prix
      </Link>

      <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
        <div>
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium">{race.circuit?.country} · Round {race.round}</p>
          <h1 className="text-3xl font-black text-white mt-1">{race.raceName}</h1>
          <p className="text-zinc-500 text-sm mt-0.5">{race.circuit?.circuitName} · {race.circuit?.locality}</p>
          {deadline && (
            <p className="text-xs text-zinc-500 mt-2">
              Deadline : {deadline.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          {isLocked && (
            <p className="mt-3 text-sm text-orange-400 bg-orange-900/20 border border-orange-800 rounded-lg px-3 py-2 inline-block">
              Les pronostics sont verrouillés pour cette course.
            </p>
          )}
        </div>
        <CircuitMap circuit={race.circuit} className="h-44" showLabel={false} />
      </div>

      <SuccessOverlay
        visible={success}
        message="Pronostic enregistré !"
        onDone={() => setSuccess(false)}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}

        {!isLocked && (
          <>
          <style>{`@keyframes d-flash{0%{opacity:0;transform:scale(.97)}18%{opacity:1;transform:scale(1.01)}100%{opacity:0;transform:scale(1.04)}}@keyframes d-ring{0%{opacity:.85;transform:translate(-50%,-50%) scale(.5)}100%{opacity:0;transform:translate(-50%,-50%) scale(2.8)}}@keyframes d-spark{0%{opacity:1;transform:translate(-50%,-50%) rotate(var(--a)) translateY(0) scale(1.2)}100%{opacity:0;transform:translate(-50%,-50%) rotate(var(--a)) translateY(-52px) scale(.3)}}`}</style>
          <div className={`relative rounded-2xl border-2 overflow-visible transition-all ${isDivine ? 'border-yellow-400/60' : 'border-zinc-700'}`}
            style={isDivine ? { background: 'linear-gradient(135deg,#0c0c10 0%,#78350f18 100%)' } : { background: 'rgba(255,255,255,0.02)' }}>
            {isDivine && (
              <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#facc15,#f97316,#facc15)', backgroundSize:'200% 100%' }} />
            )}
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                {isDivine ? (
                  <>
                    <p className="text-yellow-400 font-black text-sm leading-tight">Mode Divin activé</p>
                    <p className="text-yellow-400/60 text-xs mt-0.5">
                      Picks aléatoires · <span className="font-black text-yellow-400">×4 pts</span> par pick correct · <span className="font-bold">0 pénalité</span> si raté
                    </p>
                    <p className="text-zinc-500 text-[10px] mt-0.5">Modifier un pick manuellement désactive le mode divin</p>
                  </>
                ) : (
                  <>
                    <p className="text-white font-bold text-sm leading-tight">C'est Dieu Qui Décide</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Remplis tous tes picks aléatoirement · ×4 pts si correct · Reclique pour changer</p>
                  </>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {isDivine && (
                  <button
                    type="button"
                    onClick={cancelDivine}
                    className="px-3 py-2.5 rounded-xl font-black text-sm border border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-400 transition-all">
                    ✕ Annuler
                  </button>
                )}
                <button
                  type="button"
                  onClick={divineRoll}
                  disabled={!drivers.length}
                  className="px-4 py-2.5 rounded-xl font-black text-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
                  style={isDivine
                    ? { background: 'linear-gradient(135deg,#ca8a04,#d97706)', color: '#fff', boxShadow: '0 0 20px #ca8a0450' }
                    : { background: 'linear-gradient(135deg,#1c1917,#292524)', color: '#d4d4d8', border: '1px solid #3f3f46' }
                  }>
                  {isDivine ? 'Relancer' : 'Activer'}
                </button>
              </div>
            </div>
          {divineFlash && (
            <div style={{ position:'absolute', inset:0, zIndex:20, borderRadius:'1rem', pointerEvents:'none', overflow:'hidden' }}>
              <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 60% 50%,#fbbf2480 0%,#f59e0b22 55%,transparent 100%)', animation:'d-flash .9s ease-out forwards' }} />
              <div style={{ position:'absolute', top:'50%', left:'60%', width:200, height:200, borderRadius:'50%', border:'2px solid #fbbf2488', animation:'d-ring .7s ease-out forwards' }} />
              {[0,45,90,135,180,225,270,315].map((angle, i) => (
                <div key={i} style={{ '--a':`${angle}deg`, position:'absolute', top:'50%', left:'60%', fontSize:14, lineHeight:1, animation:`d-spark .65s ${i*35}ms ease-out forwards`, opacity:0 }}>{'⚡'}</div>
              ))}
            </div>
          )}
          </div>
          </>
        )}

        <Section title={<><Icon name="trophy" size={15} className="text-yellow-400" />Podium</>}>
          <PodiumPicker
            winner={picks.winner}
            p2={picks.p2}
            p3={picks.p3}
            onChange={({ winner, p2, p3 }) => { setPicks(p => ({ ...p, winner, p2, p3 })); setIsDivine(false); }}
            drivers={drivers}
            disabled={isLocked}
          />
        </Section>

        <Section title={<><Icon name="lightning" size={15} className="text-blue-400" />Qualifications</>}>
          <DriverCardPicker label="Pole Position" value={picks.polePosition} onChange={set('polePosition')} drivers={drivers} disabled={isLocked} />
          <DriverCardPicker label="Meilleur tour en qualif" value={picks.fastestQualifyingLap} onChange={set('fastestQualifyingLap')} drivers={drivers} disabled={isLocked} />
          <DriverCardPicker label="Pilotes éliminés en Q1" value={picks.q1Eliminated} onChange={set('q1Eliminated')} drivers={drivers} disabled={isLocked} multi max={5} />
          <DriverCardPicker label="Pilotes éliminés en Q2" value={picks.q2Eliminated} onChange={set('q2Eliminated')} drivers={drivers} disabled={isLocked} multi max={5} disabledIds={picks.q1Eliminated} />
        </Section>

        <Section title={<><Icon name="flag" size={15} className="text-zinc-300" />Course</>}>
          <DriverCardPicker label="Meilleur tour en course" value={picks.fastestLap} onChange={set('fastestLap')} drivers={drivers} disabled={isLocked} />
        </Section>

        <Section title={<><Icon name="flag" size={15} className="text-red-400" />Événements course</>}>
          <YesNoToggle label="Safety Car" value={picks.safetyCar} onChange={set('safetyCar')} disabled={isLocked} points={4} />
          <div className="border-t border-zinc-800/60" />
          <YesNoToggle label="Virtual Safety Car" value={picks.virtualSafetyCar} onChange={set('virtualSafetyCar')} disabled={isLocked} points={2} />
          <div className="border-t border-zinc-800/60" />
          <YesNoToggle label="Drapeau rouge" value={picks.redFlag} onChange={set('redFlag')} disabled={isLocked} points={10} />
        </Section>

        <Section title={<><Icon name="lightning" size={13} /> Boosts de risque</>} defaultOpen={false}>
          <p className="text-zinc-500 text-xs -mt-2 mb-1">Multiplie tes gains — mais risque une pénalité si tu te trompes. Les boosts sont verrouillés avec tes pronos.</p>
          {BOOST_META.map(meta => (
            <BoostRow key={meta.key} meta={meta} value={boosts[meta.key]} onChange={(v) => setBoosts(b => ({ ...b, [meta.key]: v }))} disabled={isLocked} />
          ))}
        </Section>

        {!isLocked && (
          <button type="submit" className="btn-primary py-3 text-base" disabled={saving}>
            {saving ? 'Enregistrement...' : 'Sauvegarder mon pronostic'}
          </button>
        )}
      </form>

      {/* Replay section — shown after race is scored */}
      {isPast && isScored && (
        <PronosticReplay
          picks={picks}
          sprintPicks={sprintPicks}
          boosts={boosts}
          isDivine={isDivine}
          score={score}
          sprintScore={sprintScore}
          race={race}
          liveResults={liveResults}
          drivers={drivers}
        />
      )}
      {isPast && !isScored && (
        <div className="mt-8 card text-center py-10 text-zinc-500">
          <p className="text-4xl mb-3">⏳</p>
          <p className="font-bold text-white mb-1">Résultats en attente</p>
          <p className="text-sm">Le score sera calculé une fois les résultats officiels saisis.</p>
        </div>
      )}

      {hasSprint && (
        <form onSubmit={handleSprintSubmit} className="flex flex-col gap-6 mt-8">
          <div className="border-t border-zinc-800 pt-6">
            <h2 className="text-xl font-black text-white flex items-center gap-2 mb-1">
              <Icon name="lightning" size={16} /> Pronostic Sprint
            </h2>
            <p className="text-zinc-500 text-sm mb-2">
              Vainqueur&nbsp;·&nbsp;12 pts &nbsp;|&nbsp; 2e&nbsp;·&nbsp;10 pts &nbsp;|&nbsp; 3e&nbsp;·&nbsp;8 pts
            </p>
            {sprintDeadline && (
              <p className="text-xs text-zinc-500 mb-3">
                Deadline sprint : {sprintDeadline.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
            {isSprintLocked && (
              <p className="text-sm text-orange-400 bg-orange-900/20 border border-orange-800 rounded-lg px-3 py-2 inline-block mb-3">
                Pronostics sprint verrouillés.
              </p>
            )}
          </div>

          {sprintError && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{sprintError}</p>}

          <SuccessOverlay visible={sprintSuccess} message="Sprint enregistré !" onDone={() => setSprintSuccess(false)} />

          <Section title={<><Icon name="flag" size={13} /> Podium Sprint</>}>
            <PodiumPicker
              winner={sprintPicks.winner}
              p2={sprintPicks.p2}
              p3={sprintPicks.p3}
              onChange={({ winner, p2, p3 }) => setSprintPicks({ winner, p2, p3 })}
              drivers={drivers}
              disabled={isSprintLocked}
            />
          </Section>

          {!isSprintLocked && (
            <button type="submit" className="btn-primary py-3 text-base" style={{ background: '#ea580c' }} disabled={savingSprint}>
              {savingSprint ? 'Enregistrement...' : 'Sauvegarder mon pronostic sprint'}
            </button>
          )}
        </form>
      )}
    </div>
  );
};

export default Pronostic;
